import type { RawImage } from "../../../../types/api/wcStoreApi/wcStoreApiProductsEndpointTypes";
import type {
  TransformedProduct, // Keep if needed for context, but direct dependencies are better
  TransformedVariation,
} from "../types/productApiTypes";

/**
 * The default WooCommerce placeholder image path
 */
export const PLACEHOLDER_IMAGE_URL =
  "/wp-content/uploads/woocommerce-placeholder.png";

/**
 * Checks if an image object is valid (has a src property)
 * @param image The image object to validate
 * @returns Boolean indicating if the image is valid
 */
export const isValidImage = (image: any): image is RawImage => {
  return (
    image && typeof image === "object" && "src" in image && Boolean(image.src)
  );
};

/**
 * Gets combined gallery images based on product type and selected variation,
 * implementing the desired display priority.
 *
 * @param productImages Base product images array (from parent product).
 * @param selectedVariation Selected variation object (if any).
 * @param variationsLoading Whether variations are currently loading (optional).
 * @returns Array of Image objects to use in the gallery in the correct order.
 */
export const getGalleryImages = (
  productImages: RawImage[] = [],
  selectedVariation: TransformedVariation | null = null,
  variationsLoading: boolean = false // Optional: useful for transitions
): RawImage[] => {
  const parentGallery = Array.isArray(productImages)
    ? productImages.filter(isValidImage)
    : [];

  // --- Handle Loading State ---
  // If variations are loading, show the parent gallery temporarily to avoid flicker
  if (variationsLoading && selectedVariation) {
    return parentGallery;
  }

  // --- Handle Variation Selected ---
  if (selectedVariation) {
    const finalGallery: RawImage[] = [];
    const addedImageIds = new Set<number>();

    // 1. Add Variation's Main Image (if valid)
    const variationImage = selectedVariation.image as RawImage | undefined; // Cast for type checking
    if (isValidImage(variationImage)) {
      finalGallery.push(variationImage);
      addedImageIds.add(variationImage.id);
    }

    // 2. Add Variation's Specific Gallery Images (if valid and not already added)
    const variationGalleryImages = selectedVariation.variationGallery || [];
    variationGalleryImages.forEach((img) => {
      if (isValidImage(img) && !addedImageIds.has(img.id)) {
        finalGallery.push(img);
        addedImageIds.add(img.id);
      }
    });

    // 3. Add Parent Product's Gallery Images (if valid and not already added)
    parentGallery.forEach((img) => {
      if (isValidImage(img) && !addedImageIds.has(img.id)) {
        finalGallery.push(img);
        addedImageIds.add(img.id); // Add here too, although technically not needed for subsequent steps
      }
    });

    // 4. Fallback: If variation exists but resulted in an empty gallery (no specific images)
    //    show the parent gallery instead.
    if (finalGallery.length > 0) {
      return finalGallery;
    } else {
      // Variation was selected, but it and its gallery had no valid images.
      // Fall back to parent gallery.
      return parentGallery;
    }
  }

  // --- Handle No Variation Selected (or Simple Product) ---
  // Return the parent product's gallery
  return parentGallery;
};

/**
 * Checks if a gallery of images is empty or contains no valid images
 * @param images Array of images to check
 * @returns Boolean indicating if the gallery is effectively empty
 */
export const isGalleryEmpty = (images: RawImage[]): boolean => {
  if (!images || images.length === 0) return true;
  const validImages = images.filter(isValidImage);
  return validImages.length === 0;
};

/**
 * Checks if a product has valid images in its main 'images' array
 * @param product The product object to check
 * @returns Boolean indicating if the product has valid images
 */
export const hasProductImages = (
  product: TransformedProduct | null | undefined
): boolean => {
  if (
    !product?.images ||
    !Array.isArray(product.images) ||
    product.images.length === 0
  ) {
    return false;
  }
  return product.images.some(isValidImage);
};

/**
 * Gets the *first* valid image source URL from a product's main gallery, with fallback.
 * Primarily used for single image displays like product cards where only one image is needed initially.
 * @param product The product object to get an image from
 * @returns URL string for the image source
 */
export const getProductImageSource = (
  product: TransformedProduct | null | undefined
): string => {
  if (!product) return PLACEHOLDER_IMAGE_URL;
  const validImage = product.images?.find(isValidImage);
  return validImage?.src || PLACEHOLDER_IMAGE_URL;
};

/**
 * Gets an appropriate single image source for display (e.g., product card thumbnail).
 * Prioritizes variation image if available, otherwise uses the first parent image.
 * @param product The product to get an image for
 * @param variation Optional variation that might have its own image
 * @returns URL string for the image source
 */
export const getDisplayImageSource = (
  product: TransformedProduct | null | undefined,
  variation: TransformedVariation | null = null
): string => {
  // 1. Try Variation Image first
  if (variation && isValidImage(variation.image)) {
    return (variation.image as RawImage).src;
  }
  // 2. Fallback to the first valid image from the parent product's gallery
  return getProductImageSource(product);
};
